import React, { useState, useMemo, useCallback } from 'react'
import { Plus, Search, Filter, Wrench, IndianRupee, Calendar, Clock, Edit, Trash2, Truck, FileBarChart, Download, Package } from 'lucide-react'
import jsPDF from 'jspdf'
import 'jspdf-autotable'
import { useData } from '../context/DataContext'
import ServiceForm from '../components/ServiceForm'
import Modal from '../components/Modal'
import DeleteConfirmModal from '../components/DeleteConfirmModal'

function Services() {
  const { state, addService, updateService, deleteService } = useData()
  const { services, vehicles, loading, error } = state



  const [showForm, setShowForm] = useState(false)
  const [editingService, setEditingService] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterVehicle, setFilterVehicle] = useState('all')
  const [filterServiceType, setFilterServiceType] = useState('all')
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, service: null })
  const [isStatementModalOpen, setIsStatementModalOpen] = useState(false)
  const [statementStartDate, setStatementStartDate] = useState('')
  const [statementEndDate, setStatementEndDate] = useState('')
  const [statementPaymentStatus, setStatementPaymentStatus] = useState('all')
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false)

  const filteredServices = useMemo(() => {
    return services.filter(service => {
      // Find the vehicle for this service
      const vehicle = vehicles.find(v => v.id === service.vehicle_id)
      const vehicleNumber = vehicle?.registrationNumber || ''
      
      const matchesSearch = service.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           vehicleNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           vehicle?.type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           vehicle?.make?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           vehicle?.model?.toLowerCase().includes(searchTerm.toLowerCase())
      const matchesVehicle = filterVehicle === 'all' || service.vehicle_id === filterVehicle
      const matchesServiceType = filterServiceType === 'all' || service.service_type === filterServiceType
      
      // Date range filtering
      let matchesDateRange = true
      if (fromDate || toDate) {
        const serviceDate = new Date(service.service_date)
        if (fromDate) {
          const fromDateObj = new Date(fromDate)
          matchesDateRange = matchesDateRange && serviceDate >= fromDateObj
        }
        if (toDate) {
          const toDateObj = new Date(toDate)
          toDateObj.setHours(23, 59, 59, 999) // Include the entire day
          matchesDateRange = matchesDateRange && serviceDate <= toDateObj
        }
      }
      
      return matchesSearch && matchesVehicle && matchesServiceType && matchesDateRange
    })
  }, [services, searchTerm, filterVehicle, filterServiceType, fromDate, toDate])

  const handleSubmit = useCallback(async (serviceData) => {
    try {
      if (editingService) {
        const result = await updateService(editingService.id, serviceData)
        if (result.success) {
          setShowForm(false)
          setEditingService(null)
        }
      } else {
        const result = await addService(serviceData)
        if (result.success) {
          setShowForm(false)
          setEditingService(null)
        }
      }
    } catch (error) {
      console.error('Error submitting service:', error)
    }
  }, [editingService, addService, updateService])

  const handleEditService = useCallback((service) => {
    setEditingService(service)
    setShowForm(true)
  }, [])

  const handleDeleteService = useCallback(async (serviceId) => {
    try {
      const result = await deleteService(serviceId)
      if (!result.success) {
        alert(`Error deleting service: ${result.error}`)
      }
    } catch (error) {
      console.error('Error deleting service:', error)
      alert('Error deleting service. Please try again.')
    }
  }, [deleteService])

  const openDeleteModal = useCallback((service) => {
    setDeleteModal({ isOpen: true, service })
  }, [])

  const closeDeleteModal = useCallback(() => {
    setDeleteModal({ isOpen: false, service: null })
  }, [])

  const confirmDelete = useCallback(() => {
    if (deleteModal.service) {
      handleDeleteService(deleteModal.service.id)
    }
  }, [deleteModal.service, handleDeleteService])

  const handleCancelEdit = useCallback(() => {
    setShowForm(false)
    setEditingService(null)
  }, [])

  // Show loading state during operations
  const isUpdating = loading && editingService
  const isCreating = loading && !editingService && showForm

  const totalAmountSpent = filteredServices.reduce((total, service) => total + (service.amount_spent || 0), 0)
  const totalLabourAmount = filteredServices.reduce((total, service) => total + (service.labour_amount || 0), 0)
  const totalPartsCost = totalAmountSpent - totalLabourAmount

  // Calculate vehicle statement when a specific vehicle is selected or all vehicles
  const getVehicleStatement = () => {
    let vehicleServices = []
    let vehicle = null
    
    if (filterVehicle === 'all') {
      // All vehicles statement
      vehicleServices = services
      vehicle = { registrationNumber: 'All Vehicles', type: 'Combined Statement', make: 'N/A', model: 'N/A' }
    } else {
      // Specific vehicle statement
      vehicleServices = services.filter(service => service.vehicle_id === filterVehicle)
      vehicle = vehicles.find(v => v.id === filterVehicle)
      
      if (!vehicle || vehicleServices.length === 0) return null
    }
    
    // Apply date range filter if dates are selected
    if (statementStartDate || statementEndDate) {
      vehicleServices = vehicleServices.filter(service => {
        const serviceDate = new Date(service.service_date)
        const start = statementStartDate ? new Date(statementStartDate) : null
        const end = statementEndDate ? new Date(statementEndDate) : null
        
        if (start && end) {
          return serviceDate >= start && serviceDate <= end
        } else if (start) {
          return serviceDate >= start
        } else if (end) {
          return serviceDate <= end
        }
        return true
      })
    }
    
    // Apply payment status filter if selected
    if (statementPaymentStatus !== 'all') {
      vehicleServices = vehicleServices.filter(service => service.payment_status === statementPaymentStatus)
    }
    
    const totalRecords = vehicleServices.length
    const totalAmountSpent = vehicleServices.reduce((sum, service) => sum + parseFloat(service.amount_spent || 0), 0)
    const totalLabourAmount = vehicleServices.reduce((sum, service) => sum + parseFloat(service.labour_amount || 0), 0)
    const totalPartsCost = totalAmountSpent - totalLabourAmount
    
    // Group services by type
    const servicesByType = vehicleServices.reduce((acc, service) => {
      const type = service.service_type || 'other'
      if (!acc[type]) acc[type] = []
      acc[type].push(service)
      return acc
    }, {})
    
    return {
      vehicle,
      totalRecords,
      totalAmountSpent,
      totalLabourAmount,
      totalPartsCost,
      vehicleServices,
      servicesByType
    }
  }

  const vehicleStatement = getVehicleStatement()

  // Generate PDF for vehicle statement
  const generatePDF = async (statement) => {
    setIsGeneratingPDF(true)
    try {
      console.log('Starting PDF generation...')
      
      // Check if running on mobile device
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
      console.log('Is mobile device:', isMobile)
      
      const doc = new jsPDF()
      console.log('jsPDF instance created')
      
      // Set font
      doc.setFont('helvetica')
      
      // Header
      doc.setFontSize(20)
      doc.setTextColor(255, 69, 0) // Orange color (website theme)
      doc.text('Sri Vemurugan Transport and Earthmovers', 105, 20, { align: 'center' })
      
      doc.setFontSize(16)
      doc.setTextColor(0, 0, 0)
      doc.text('Vehicle Service Statement', 105, 35, { align: 'center' })
      
      // Add line separator after heading
      doc.setDrawColor(200, 200, 200)
      doc.setLineWidth(0.5)
      doc.line(20, 42, 190, 42)
      
      // Date Range Info
      if (statementStartDate || statementEndDate) {
        doc.setFontSize(12)
        doc.setTextColor(128, 128, 128)
        let dateRangeText = 'Period: '
        if (statementStartDate && statementEndDate) {
          dateRangeText += `${new Date(statementStartDate).toLocaleDateString()} to ${new Date(statementEndDate).toLocaleDateString()}`
        } else if (statementStartDate) {
          dateRangeText += `From ${new Date(statementStartDate).toLocaleDateString()}`
        } else if (statementEndDate) {
          dateRangeText += `Until ${new Date(statementEndDate).toLocaleDateString()}`
        }
        doc.text(dateRangeText, 105, 50, { align: 'center' })
      }
      
      // Vehicle Information
      const startY = statementStartDate || statementEndDate ? 65 : 55
      doc.setFontSize(14)
      doc.setTextColor(255, 69, 0)
      doc.text('Vehicle Details:', 20, startY)
      
      doc.setFontSize(12)
      doc.setTextColor(0, 0, 0)
      doc.text(`Registration: ${statement.vehicle.registrationNumber}`, 20, startY + 10)
      doc.text(`Type: ${statement.vehicle.type}`, 20, startY + 20)
      doc.text(`Make: ${statement.vehicle.make || 'N/A'}`, 20, startY + 30)
      doc.text(`Model: ${statement.vehicle.model || 'N/A'}`, 20, startY + 40)
      
      // Add line separator after vehicle details
      doc.setDrawColor(200, 200, 200)
      doc.setLineWidth(0.5)
      doc.line(20, startY + 50, 190, startY + 50)
      
      // Summary Statistics
      const statsStartY = startY + 60
      doc.setFontSize(14)
      doc.setTextColor(255, 69, 0)
      doc.text('Summary Statistics:', 20, statsStartY)
      
      doc.setFontSize(10)
      doc.setTextColor(0, 0, 0)
      doc.text(`Total Services: ${statement.totalRecords}`, 20, statsStartY + 10)
      doc.text(`Total Amount Spent: ${statement.totalAmountSpent.toFixed(2)}`, 20, statsStartY + 20)
      doc.text(`Labour Cost: ${statement.totalLabourAmount.toFixed(2)}`, 20, statsStartY + 30)
      doc.text(`Parts Cost: ${statement.totalPartsCost.toFixed(2)}`, 20, statsStartY + 40)
      
      // Add line separator after summary statistics
      doc.setDrawColor(200, 200, 200)
      doc.setLineWidth(0.5)
      doc.line(20, statsStartY + 50, 190, statsStartY + 50)
      
      // Services Table
      if (statement.vehicleServices.length > 0) {
        const tableStartY = statsStartY + 60
        doc.setFontSize(14)
        doc.setTextColor(255, 69, 0)
        doc.text('Service Details:', 20, tableStartY)
        
        const tableData = statement.vehicleServices.map((service, index) => {
          const partsCost = parseFloat(service.amount_spent || 0) - parseFloat(service.labour_amount || 0)
          return [
            new Date(service.service_date).toLocaleDateString(),
            service.description || 'N/A',
            service.service_type || 'N/A',
            service.labour_amount ? parseFloat(service.labour_amount).toFixed(2) : '0.00',
            partsCost.toFixed(2),
            service.amount_spent ? parseFloat(service.amount_spent).toFixed(2) : '0.00',
            service.store_name || 'N/A',
            service.parts_brand || 'N/A'
          ]
        })
        
        doc.autoTable({
          startY: tableStartY + 10,
          head: [['Date', 'Description', 'Service\nType', 'Labour\nAmount', 'Parts\nCost', 'Total\nAmount', 'Store\nName', 'Parts\nBrand']],
          body: tableData,
          theme: 'grid',
          headStyles: {
            fillColor: [255, 69, 0],
            textColor: 255,
            fontSize: 8,
            halign: 'center',
            valign: 'middle',
            lineHeight: 1.2
          },
          bodyStyles: {
            fontSize: 8
          },
          columnStyles: {
            0: { cellWidth: 18 }, // Date
            1: { cellWidth: 25 }, // Description
            2: { cellWidth: 18 }, // Service Type
            3: { cellWidth: 18 }, // Labour Amount
            4: { cellWidth: 18 }, // Parts Cost
            5: { cellWidth: 18 }, // Total Amount
            6: { cellWidth: 20 }, // Store Name
            7: { cellWidth: 20 }  // Parts Brand
          },
          margin: { top: 10 }
        })
      }
      
      // Footer
      const pageCount = doc.internal.getNumberOfPages()
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i)
        doc.setFontSize(10)
        doc.setTextColor(128, 128, 128)
        doc.text(`Page ${i} of ${pageCount}`, 105, doc.internal.pageSize.height - 10, { align: 'center' })
        doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 105, doc.internal.pageSize.height - 5, { align: 'center' })
      }
      
      // Save the PDF
      let fileName = `Vehicle_Service_Statement_${statement.vehicle.registrationNumber.replace(/[^a-zA-Z0-9]/g, '_')}`
      if (statementStartDate || statementEndDate) {
        if (statementStartDate && statementEndDate) {
          fileName += `_${statementStartDate}_to_${statementEndDate}`
        } else if (statementStartDate) {
          fileName += `_from_${statementStartDate}`
        } else if (statementEndDate) {
          fileName += `_until_${statementEndDate}`
        }
      }
      fileName += `_${new Date().toISOString().split('T')[0]}.pdf`
      
      console.log('Saving PDF:', fileName)
      
      // Enhanced mobile compatibility
      if (isMobile) {
        try {
          // For mobile devices, try multiple approaches
          const pdfBlob = doc.output('blob')
          const url = URL.createObjectURL(pdfBlob)
          
          // Method 1: Try direct download
          const link = document.createElement('a')
          link.href = url
          link.download = fileName
          link.style.display = 'none'
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          
          // Method 2: If direct download fails, open in new tab
          setTimeout(() => {
            try {
              window.open(url, '_blank')
            } catch (e) {
              console.log('Failed to open in new tab:', e)
              // Method 3: Show user instructions
              alert('PDF generated successfully! Please use your browser\'s "Save as" option to download the file.')
            }
          }, 1000)
          
          URL.revokeObjectURL(url)
          console.log('PDF saved successfully on mobile')
        } catch (mobileError) {
          console.error('Mobile download failed:', mobileError)
          // Fallback: Try standard save method
          try {
            doc.save(fileName)
            console.log('PDF saved using fallback method')
          } catch (fallbackError) {
            console.error('Fallback also failed:', fallbackError)
            alert('PDF generation completed. Please check your downloads folder or use browser save options.')
          }
        }
      } else {
        // For desktop, use standard save method
        doc.save(fileName)
        console.log('PDF saved successfully on desktop')
      }
      
    } catch (error) {
      console.error('Error generating PDF:', error)
      alert(`Error generating PDF: ${error.message}`)
    } finally {
      setIsGeneratingPDF(false)
    }
  }

  return (
    <div className="container mx-auto px-4 py-2">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Services Management</h1>
          <p className="text-gray-600">Track vehicle maintenance and service records</p>
        </div>
        <div className="mt-4 sm:mt-0 flex flex-col sm:flex-row gap-3">
        <button
          onClick={() => setShowForm(true)}
            className="btn btn-primary flex items-center space-x-2"
        >
            <Plus className="h-5 w-5" />
            <span>Add Service</span>
        </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <div className="bg-white p-3 sm:p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Wrench className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600" />
            </div>
            <div className="ml-2 sm:ml-3">
              <p className="text-sm font-medium text-gray-600">Total Services</p>
              <p className="text-base sm:text-lg font-semibold text-gray-900">{filteredServices.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-3 sm:p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="p-2 bg-red-100 rounded-lg">
              <IndianRupee className="h-4 w-4 sm:h-5 sm:w-5 text-red-600" />
            </div>
            <div className="ml-2 sm:ml-3">
              <p className="text-sm font-medium text-gray-600">Total Spent</p>
              <p className="text-base sm:text-lg font-semibold text-gray-900">₹{totalAmountSpent.toFixed(2)}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-3 sm:p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <IndianRupee className="h-4 w-4 sm:h-5 sm:w-5 text-green-600" />
            </div>
            <div className="ml-2 sm:ml-3">
              <p className="text-sm font-medium text-gray-600">Labour Cost</p>
              <p className="text-base sm:text-lg font-semibold text-gray-900">₹{totalLabourAmount.toFixed(2)}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-3 sm:p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 rounded-lg">
              <IndianRupee className="h-4 w-4 sm:h-5 sm:w-5 text-purple-600" />
            </div>
            <div className="ml-2 sm:ml-3">
              <p className="text-sm font-medium text-gray-600">Parts Cost</p>
              <p className="text-base sm:text-lg font-semibold text-gray-900">₹{totalPartsCost.toFixed(2)}</p>
            </div>
          </div>
        </div>
      </div>

             {/* Filters */}
       <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 my-6">
         <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by description, vehicle number, type, make, model..."
                className="filter-input pl-10"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Vehicle</label>
            <div className="flex space-x-2">
              <select
                value={filterVehicle}
                onChange={(e) => setFilterVehicle(e.target.value)}
                className="filter-input text-gray-900 flex-1"
              >
                <option value="all" className="text-gray-900">All Vehicles</option>
                {vehicles.map(vehicle => (
                  <option key={vehicle.id} value={vehicle.id} className="text-gray-900">
                    {vehicle.registrationNumber} - {vehicle.type}
                  </option>
                ))}
              </select>
              {vehicleStatement && (
                <button
                  onClick={() => setIsStatementModalOpen(true)}
                  className="p-2 bg-gradient-to-r from-brand-orange to-brand-red text-white rounded-lg hover:from-orange-500 hover:to-red-600 transition-all duration-200"
                  title={filterVehicle === 'all' ? "View All Vehicles Statement" : "View Vehicle Service Statement"}
                >
                  <FileBarChart className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Service Type</label>
            <select
              value={filterServiceType}
              onChange={(e) => setFilterServiceType(e.target.value)}
              className="filter-input text-gray-900"
            >
              <option value="all" className="text-gray-900">All Types</option>
              <option value="oil" className="text-gray-900">Oil Service</option>
              <option value="spares" className="text-gray-900">Spares/Repairs</option>
              <option value="general" className="text-gray-900">General Service</option>
              <option value="emergency" className="text-gray-900">Emergency Repair</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="filter-input text-gray-900"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="filter-input text-gray-900"
            />
          </div>
        </div>
        
        {/* Clear Filters Button */}
        <div className="mt-4 flex justify-end">
          <button
            onClick={() => {
              setSearchTerm('')
              setFilterVehicle('all')
              setFilterServiceType('all')
              setFromDate('')
              setToDate('')
            }}
            className="px-4 py-2 text-sm bg-gradient-to-r from-brand-orange to-brand-red text-white rounded-lg hover:from-orange-500 hover:to-red-600 transition-all duration-200 font-medium"
          >
            Clear All Filters
          </button>
        </div>
      </div>

      {/* Services Cards */}
      <div className="space-y-4">
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-red mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading services...</p>
          </div>
        ) : filteredServices.length === 0 ? (
          <div className="col-span-full">
            <div className="text-center py-12">
              <div className="bg-gray-100 rounded-full p-3 w-16 h-16 mx-auto mb-4">
                <Plus className="h-10 w-10 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {searchTerm ? 'No services found' : 'No services yet'}
              </h3>
              <p className="text-gray-500 mb-4">
                {searchTerm || fromDate || toDate
                  ? 'Try adjusting your search criteria or date filters' 
                  : 'Get started by adding your first service'
                }
              </p>
              {!searchTerm && !fromDate && !toDate && (
            <button
              onClick={() => setShowForm(true)}
                  className="btn btn-primary"
            >
                  Add Your First Service
            </button>
              )}
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredServices.map((service) => {
                  const vehicle = vehicles.find(v => v.id === service.vehicle_id)
                  return (
                <div key={service.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow duration-200 flex flex-col h-full">
                  {/* Service Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">
                              {vehicle?.registrationNumber || 'Unknown Vehicle'}
                      </h3>
                      <p className="text-sm text-gray-600 mb-2">
                              {vehicle?.type} - {vehicle?.brand} {vehicle?.model}
                      </p>
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              service.service_type === 'oil' ? 'bg-blue-100 text-blue-800' :
                              service.service_type === 'spares' ? 'bg-red-100 text-red-800' :
                              service.service_type === 'general' ? 'bg-green-100 text-green-800' :
                              'bg-orange-100 text-orange-800'
                            }`}>
                              {service.service_type === 'oil' ? 'Oil Service' :
                               service.service_type === 'spares' ? 'Spares/Repairs' :
                               service.service_type === 'general' ? 'General Service' :
                               'Emergency Repair'}
                            </span>
                          </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleEditService(service)}
                        className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-full transition-colors"
                        title="Edit service"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      {/* Delete button hidden */}
                      {/* <button
                        onClick={() => openDeleteModal(service)}
                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-full transition-colors"
                        title="Delete service"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button> */}
                        </div>
                  </div>

                  {/* Service Information */}
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center text-sm text-gray-600">
                            <Calendar className="h-4 w-4 mr-2 text-gray-400" />
                      <span>{new Date(service.service_date).toLocaleDateString()}</span>
                          </div>
                    <div className="flex items-center text-sm text-gray-600">
                            <Clock className="h-4 w-4 mr-2 text-gray-400" />
                      <span>{service.vehicle_hours} hours</span>
                          </div>
                    <div className="flex items-center text-sm text-gray-600">
                            <IndianRupee className="h-4 w-4 mr-2 text-gray-400" />
                      <span>₹{service.amount_spent?.toFixed(2)}</span>
                          </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <Wrench className="h-4 w-4 mr-2 text-gray-400" />
                      <span>Labour: ₹{service.labour_amount?.toFixed(2)}</span>
                          </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <Package className="h-4 w-4 mr-2 text-gray-400" />
                      <span>Parts: ₹{(service.amount_spent - service.labour_amount)?.toFixed(2)}</span>
                          </div>
                        </div>

                  {/* Service Details */}
                  <div className="space-y-2 mb-4 flex-grow">
                           {service.store_name && (
                      <div className="text-sm text-gray-600">
                        <span className="font-medium">Store:</span> {service.store_name}
                             </div>
                           )}
                           {service.parts_brand && (
                      <div className="text-sm text-gray-600">
                        <span className="font-medium">Brand:</span> {service.parts_brand}
                             </div>
                           )}
                    {service.description && (
                      <div className="text-sm text-gray-600">
                        <span className="font-medium">Description:</span> {service.description}
                             </div>
                           )}
                         </div>

                  {/* Service Info */}
                  <div className="text-xs text-gray-400 pt-3">
                    {/* Additional service information can be added here if needed */}
                        </div>
                  
                  {/* Payment Status */}
                  <div className="mt-auto pt-3 border-t border-gray-100 flex justify-start">
                    <span className={`inline-flex px-3 py-2 text-sm font-semibold rounded-full ${
                      service.payment_status === 'paid' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {service.payment_status === 'paid' ? 'Paid to Shop' : 'Not Paid to Shop'}
                    </span>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Service Form Modal */}
      <Modal 
        isOpen={showForm} 
        onClose={handleCancelEdit}
        title={editingService ? "Edit Service" : "Add New Service"}
        size="max-w-4xl"
      >
        <ServiceForm 
          onSubmit={handleSubmit} 
          onCancel={handleCancelEdit}
          service={editingService}
          isSubmitting={isUpdating || isCreating}
        />
      </Modal>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={closeDeleteModal}
        onConfirm={confirmDelete}
        title="Delete Service"
        message={`Are you sure you want to delete this service record?`}
        itemName={deleteModal.service?.description || 'Service'}
      />

      {/* Vehicle Service Statement Modal */}
      <Modal
        isOpen={isStatementModalOpen}
        onClose={() => {
          setIsStatementModalOpen(false)
          setStatementStartDate('')
          setStatementEndDate('')
          setStatementPaymentStatus('all')
        }}
        title={`${filterVehicle === 'all' ? 'All Vehicles' : 'Vehicle'} Service Statement - ${vehicleStatement?.vehicle?.registrationNumber || 'Vehicle'}`}
        size="max-w-6xl"
      >
        {vehicleStatement && (
          <div className="space-y-6">
            {/* Vehicle Info */}
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-4 rounded-lg border border-blue-200">
              <div className="flex items-center space-x-3">
                <Truck className="h-6 w-6 text-blue-600" />
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{vehicleStatement.vehicle.registrationNumber}</h3>
                  <p className="text-sm text-gray-600">{vehicleStatement.vehicle.type}</p>
                  {filterVehicle !== 'all' && <p className="text-sm text-gray-600">{vehicleStatement.vehicle.make} {vehicleStatement.vehicle.model}</p>}
                </div>
              </div>
            </div>

            {/* Statement Filters */}
            <div className="bg-white p-4 rounded-lg border border-gray-200">
              <h4 className="text-lg font-semibold text-gray-900 mb-3">Statement Filters</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
                  <input
                    type="date"
                    value={statementStartDate}
                    onChange={(e) => setStatementStartDate(e.target.value)}
                    className="input"
                    max={statementEndDate || undefined}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
                  <input
                    type="date"
                    value={statementEndDate}
                    onChange={(e) => setStatementEndDate(e.target.value)}
                    className="input"
                    min={statementStartDate || undefined}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Payment Status</label>
                  <select
                    value={statementPaymentStatus}
                    onChange={(e) => setStatementPaymentStatus(e.target.value)}
                    className="input"
                  >
                    <option value="all">All Status</option>
                    <option value="pending">Pending</option>
                    <option value="paid">Paid</option>
                    <option value="partial">Partial</option>
                  </select>
                </div>
              </div>
              <div className="mt-3 flex justify-between items-center">
                <p className="text-sm text-gray-600">
                  {statementStartDate || statementEndDate || statementPaymentStatus !== 'all' ? (
                    <>
                      Showing {statementPaymentStatus !== 'all' ? statementPaymentStatus : 'all'} services
                      {statementStartDate || statementEndDate ? (
                        <>
                          {' '}from{' '}
                          {statementStartDate ? new Date(statementStartDate).toLocaleDateString() : 'beginning'} to{' '}
                          {statementEndDate ? new Date(statementEndDate).toLocaleDateString() : 'end'}
                        </>
                      ) : ''}
                    </>
                  ) : (
                    `Showing all services for ${filterVehicle === 'all' ? 'all vehicles' : 'this vehicle'}`
                  )}
                </p>
                <button
                  onClick={() => {
                    setStatementStartDate('')
                    setStatementEndDate('')
                    setStatementPaymentStatus('all')
                  }}
                  className="px-3 py-1 text-sm bg-gradient-to-r from-brand-orange to-brand-red text-white rounded-lg hover:from-orange-500 hover:to-red-600 transition-all duration-200 font-medium"
                >
                  Clear Filters
                </button>
              </div>
            </div>

            {/* Summary Statistics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-white p-4 rounded-lg border border-gray-200">
                <div className="flex items-center space-x-3">
                  <div className="bg-blue-100 p-2 rounded-lg">
                    <FileBarChart className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Total Services</p>
                    <p className="text-xl font-semibold text-gray-900">{vehicleStatement.totalRecords}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-4 rounded-lg border border-gray-200">
                <div className="flex items-center space-x-3">
                  <div className="bg-red-100 p-2 rounded-lg">
                    <IndianRupee className="h-5 w-5 text-red-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Total Spent</p>
                    <p className="text-xl font-semibold text-red-600">₹{vehicleStatement.totalAmountSpent.toFixed(2)}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-4 rounded-lg border border-gray-200">
                <div className="flex items-center space-x-3">
                  <div className="bg-green-100 p-2 rounded-lg">
                    <IndianRupee className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Labour Cost</p>
                    <p className="text-xl font-semibold text-green-600">₹{vehicleStatement.totalLabourAmount.toFixed(2)}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-4 rounded-lg border border-gray-200">
                <div className="flex items-center space-x-3">
                  <div className="bg-purple-100 p-2 rounded-lg">
                    <IndianRupee className="h-5 w-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Parts Cost</p>
                    <p className="text-xl font-semibold text-purple-600">₹{vehicleStatement.totalPartsCost.toFixed(2)}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Service Details Table */}
            <div className="bg-white rounded-lg border border-gray-200">
              <div className="p-4 border-b border-gray-200">
                <h4 className="text-lg font-semibold text-gray-900">Service Details</h4>
                <p className="text-sm text-gray-600">Detailed breakdown of all services for this vehicle</p>
              </div>
              
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Service Type</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Labour Amount</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Parts Cost</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Amount</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Store Name</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Parts Brand</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payment Status</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {vehicleStatement.vehicleServices.map((service, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {new Date(service.service_date).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          <div>
                            <div className="font-medium">{service.description || 'N/A'}</div>
                            <div className="text-xs text-gray-500">{service.notes || ''}</div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">{service.service_type || 'N/A'}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">₹{parseFloat(service.labour_amount || 0).toFixed(2)}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">₹{(parseFloat(service.amount_spent || 0) - parseFloat(service.labour_amount || 0)).toFixed(2)}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">₹{parseFloat(service.amount_spent || 0).toFixed(2)}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">{service.store_name || 'N/A'}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">{service.parts_brand || 'N/A'}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            service.payment_status === 'paid' 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {service.payment_status === 'paid' ? 'Paid' : 'Not Paid'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Summary */}
            <div className="bg-gradient-to-r from-green-50 to-blue-50 p-4 rounded-lg border border-green-200">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-lg font-semibold text-gray-900">Summary</h4>
                <button
                  onClick={() => generatePDF(vehicleStatement)}
                  disabled={isGeneratingPDF}
                  className={`flex items-center space-x-2 px-4 py-2 sm:px-6 sm:py-3 bg-gradient-to-r from-brand-orange to-brand-red text-white rounded-lg hover:from-orange-500 hover:to-red-600 transition-all duration-200 font-medium touch-manipulation ${isGeneratingPDF ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {isGeneratingPDF ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Generating PDF...</span>
                    </>
                  ) : (
                    <>
                      <Download className="h-4 w-4" />
                      <span>Download PDF</span>
                    </>
                  )}
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">Total Services:</span>
                  <span className="ml-2 font-semibold text-gray-900">{vehicleStatement.totalRecords}</span>
                </div>
                <div>
                  <span className="text-gray-600">Total Spent:</span>
                  <span className="ml-2 font-semibold text-red-600">₹{vehicleStatement.totalAmountSpent.toFixed(2)}</span>
                </div>
                <div>
                  <span className="text-gray-600">Labour Cost:</span>
                  <span className="ml-2 font-semibold text-green-600">₹{vehicleStatement.totalLabourAmount.toFixed(2)}</span>
                </div>
                <div>
                  <span className="text-gray-600">Parts Cost:</span>
                  <span className="ml-2 font-semibold text-purple-600">₹{vehicleStatement.totalPartsCost.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  )
}

export default Services
